import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, User, Send, X, Shield, MessageSquare, Trash2,
  Zap, TrendingUp,
  Maximize2, Minimize2
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../../services/api';

// ─── Threat level config ───────────────────────────────────────────────────────
const THREAT_CONFIG = {
  critical: { label: 'CRITIQUE', color: 'bg-red-500', text: 'text-red-600', ring: 'ring-red-400', dot: 'bg-red-500', glow: 'shadow-red-500/40' },
  high:     { label: 'ÉLEVÉ',    color: 'bg-orange-500', text: 'text-orange-600', ring: 'ring-orange-400', dot: 'bg-orange-500', glow: 'shadow-orange-500/30' },
  medium:   { label: 'MOYEN',    color: 'bg-yellow-500', text: 'text-yellow-600', ring: 'ring-yellow-400', dot: 'bg-yellow-400', glow: 'shadow-yellow-400/20' },
  low:      { label: 'OK',       color: 'bg-emerald-500', text: 'text-emerald-600', ring: 'ring-emerald-400', dot: 'bg-emerald-500', glow: 'shadow-emerald-400/20' },
};

// ─── Typewriter hook ───────────────────────────────────────────────────────────
function useTypewriter(text, speed = 12, enabled = true) {
  const [displayed, setDisplayed] = useState(enabled ? '' : text);
  const [done, setDone] = useState(!enabled);

  useEffect(() => {
    if (!enabled) { setDisplayed(text); setDone(true); return; }
    setDisplayed('');
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, enabled]);

  return { displayed, done };
}

// ─── Message formatter ─────────────────────────────────────────────────────────
function MessageContent({ text }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-0.5">
      {lines.map((line, idx) => {
        const parts = [];
        const regex = /\*\*(.*?)\*\*/g;
        let match, lastIndex = 0;
        while ((match = regex.exec(line)) !== null) {
          if (match.index > lastIndex) parts.push(line.substring(lastIndex, match.index));
          parts.push(<strong key={match.index} className="font-semibold">{match[1]}</strong>);
          lastIndex = regex.lastIndex;
        }
        if (lastIndex < line.length) parts.push(line.substring(lastIndex));
        const content = parts.length > 0 ? parts : line;

        // Table row
        if (line.startsWith('|') && line.endsWith('|')) {
          const cells = line.split('|').filter(c => c.trim() && c.trim() !== '---');
          if (cells.length === 0) return null;
          return (
            <div key={idx} className="flex gap-2 text-xs py-0.5 border-b border-gray-100 last:border-0">
              {cells.map((c, ci) => (
                <span key={ci} className={`flex-1 ${ci === 0 ? 'text-gray-500' : 'font-medium text-gray-800'}`}>{c.trim()}</span>
              ))}
            </div>
          );
        }
        // Numbered list
        if (/^\d+\.\s/.test(line.trim())) {
          return <div key={idx} className="pl-1 text-xs leading-relaxed text-gray-700">{content}</div>;
        }
        // Bullet
        if (line.trim().startsWith('* ') || line.trim().startsWith('*   ')) {
          const clean = line.replace(/^\*\s+/, '').trim();
          return (
            <div key={idx} className="flex gap-1.5 items-start">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-indigo-400 flex-shrink-0" />
              <span className="text-xs leading-relaxed text-gray-700">{parts.length > 0 ? parts : clean}</span>
            </div>
          );
        }
        // Monospace inline
        return (
          <p key={idx} className="text-xs leading-relaxed text-gray-700 min-h-[0.4rem]">{content}</p>
        );
      })}
    </div>
  );
}

// ─── Animated bot message ──────────────────────────────────────────────────────
function BotMessage({ message, isLatest }) {
  const { displayed, done } = useTypewriter(message.text, 8, isLatest);
  const tc = THREAT_CONFIG[message.threatLevel] || THREAT_CONFIG.low;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="flex gap-2.5 justify-start"
    >
      <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0 mt-0.5">
        <Bot className="w-4 h-4" />
      </div>
      <div className="max-w-[82%] space-y-1.5">
        <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <MessageContent text={isLatest ? displayed : message.text} />
          {isLatest && !done && (
            <span className="inline-block w-1.5 h-3.5 bg-indigo-400 ml-0.5 rounded-sm animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-2 pl-1">
          <span className="text-[9px] text-gray-400">
            {new Date(message.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          {message.threatLevel && message.threatLevel !== 'low' && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white ${tc.color}`}>
              {tc.label}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── User message ──────────────────────────────────────────────────────────────
function UserMessage({ message }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="flex gap-2.5 justify-end"
    >
      <div className="max-w-[78%]">
        <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-sm">
          <p className="text-xs leading-relaxed">{message.text}</p>
        </div>
        <span className="block text-[9px] text-gray-400 text-right mt-1 pr-1">
          {new Date(message.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center text-white flex-shrink-0 mt-0.5">
        <User className="w-4 h-4" />
      </div>
    </motion.div>
  );
}

// ─── Risk meter ────────────────────────────────────────────────────────────────
function RiskMeter({ score, verdict }) {
  const pct = Math.max(score, 3);
  const color = score >= 70 ? 'bg-red-500' : score >= 40 ? 'bg-orange-400' : 'bg-emerald-500';
  const label = score >= 70 ? 'Risque élevé' : score >= 40 ? 'Risque moyen' : 'Risque faible';

  return (
    <div className="px-4 py-2.5 bg-slate-900/60 border-b border-white/10 flex-shrink-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-slate-300 font-medium">{label}</span>
        <span className="text-[10px] text-white font-bold">{score}/100</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${color} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

// ─── Typing indicator ──────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex gap-2.5 justify-start"
    >
      <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
        <Bot className="w-4 h-4" />
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1">
        {[0, 150, 300].map(d => (
          <span key={d} className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
    </motion.div>
  );
}

// ─── Quick actions ─────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { id: 'explain', label: 'Expliquer', msg: 'Explique ce score', icon: Zap, needsCtx: true },
  { id: 'protect', label: 'Protéger', msg: 'Comment me protéger ?', icon: Shield, needsCtx: false },
  { id: 'summary', label: 'Synthèse', msg: 'Synthèse des menaces', icon: TrendingUp, needsCtx: true },
];

// ─── Main Widget ───────────────────────────────────────────────────────────────
export default function ChatbotWidget() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [isOpen, setIsOpen]                   = useState(false);
  const [messages, setMessages]               = useState([]);
  const [inputMessage, setInputMessage]       = useState('');
  const [isLoading, setIsLoading]             = useState(false);
  const [activeContext, setActiveContext]     = useState(null);
  const [suggestions, setSuggestions]         = useState(["C'est quoi le phishing ?", "Comment me protéger ?", "Comment marche SENTINELLE ?"]);
  const [hasNewBadge, setHasNewBadge]         = useState(false);
  const [isFullscreen, setIsFullscreen]       = useState(false);
  const [latestBotId, setLatestBotId]         = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  // Init welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcome = {
        id: 'welcome',
        sender: 'bot',
        text: "Bonjour ! Je suis votre **Analyste Cybersécurité SENTINELLE**. 🛡️\nJe peux expliquer vos analyses, détecter le phishing, le spoofing de marque, et vous guider pour vous protéger. Que puis-je faire pour vous ?",
        timestamp: new Date(),
        threatLevel: null,
      };
      setMessages([welcome]);
      setLatestBotId('welcome');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync context from results page
  useEffect(() => {
    if (location.pathname === '/results' && location.state?.data) {
      const data = location.state.data;
      setActiveContext(data);
      if (!isOpen) setHasNewBadge(true);
      setSuggestions(['Explique ce score', 'Comment me protéger ?', 'Synthèse des menaces']);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Listen for custom open-chat event (fired from ResultsPage)
  useEffect(() => {
    const handler = (e) => {
      const { context, message } = e.detail || {};
      if (context) setActiveContext(context);
      setIsOpen(true);
      setHasNewBadge(false);
      if (message) {
        setTimeout(() => handleSendMessage(message), 300);
      }
    };
    window.addEventListener('sentinelle:open-chat', handler);
    return () => window.removeEventListener('sentinelle:open-chat', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 200);
  }, [isOpen]);

  const handleSendMessage = useCallback(async (textToSend) => {
    const text = textToSend || inputMessage;
    if (!text.trim() || isLoading) return;

    const userMsg = { id: `usr-${Date.now()}`, sender: 'user', text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    if (!textToSend) setInputMessage('');
    setIsLoading(true);

    try {
      const history = messages.slice(-12).map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));

      const response = await api.sendChatMessage(text, activeContext, history);
      const botId = `bot-${Date.now()}`;

      setMessages(prev => [...prev, {
        id: botId,
        sender: 'bot',
        text: response.reply || "Je n'ai pas pu analyser votre message.",
        timestamp: new Date(),
        threatLevel: response.threatLevel || null,
      }]);
      setLatestBotId(botId);

      if (response.suggestions?.length > 0) setSuggestions(response.suggestions);

    } catch {
      const errId = `err-${Date.now()}`;
      setMessages(prev => [...prev, {
        id: errId,
        sender: 'bot',
        text: "Désolé, je rencontre une difficulté de connexion. Veuillez réessayer dans quelques instants.",
        timestamp: new Date(),
        threatLevel: null,
      }]);
      setLatestBotId(errId);
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, messages, activeContext, isLoading]);

  const clearChat = () => {
    const id = 'reset';
    setMessages([{ id, sender: 'bot', text: "Historique réinitialisé. 🛡️ Je suis à votre écoute !", timestamp: new Date(), threatLevel: null }]);
    setLatestBotId(id);
    setSuggestions(["C'est quoi le phishing ?", "Comment me protéger ?", "Comment marche SENTINELLE ?"]);
  };

  const toggleChat = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) setHasNewBadge(false);
  };

  const ctxScore   = activeContext?.score ?? 0;
  const ctxVerdict = activeContext?.verdict ?? 'safe';
  const ctxThreat  = activeContext ? (ctxScore >= 70 ? 'critical' : ctxScore >= 40 ? 'high' : 'low') : null;
  const tc         = ctxThreat ? THREAT_CONFIG[ctxThreat] : null;

  if (!isAuthenticated) return null;

  return (
    <div className={isFullscreen && isOpen ? 'fixed inset-0 z-50 flex flex-col' : 'fixed bottom-[5.5rem] right-4 md:bottom-6 md:right-6 z-50 flex flex-col items-end'}>

      {/* ── Chat window ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: isFullscreen ? 0 : 24, scale: isFullscreen ? 1 : 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: isFullscreen ? 0 : 24, scale: isFullscreen ? 1 : 0.96 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className={isFullscreen
              ? 'w-full h-full bg-white flex flex-col overflow-hidden'
              : 'w-[calc(100vw-2rem)] max-w-[370px] md:max-w-[400px] h-[560px] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden mb-4 border border-gray-100'
            }
          >
            {/* Header */}
            <header className="bg-gradient-to-r from-slate-900 via-indigo-900 to-blue-900 px-5 py-4 text-white flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-sky-300" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 border-2 border-indigo-900 rounded-full animate-pulse" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-wide">Analyste SENTINELLE</h3>
                  <span className="text-[10px] text-sky-300/80">
                    {activeContext ? `Contexte actif — ${activeContext.type?.toUpperCase()}` : 'Veille active de sécurité'}
                  </span>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={clearChat} title="Reinitialiser" className="p-1.5 hover:bg-white/10 rounded-xl transition-colors text-white/60 hover:text-white" aria-label="Effacer l'historique">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => setIsFullscreen(f => !f)} title={isFullscreen ? 'Reduire' : 'Plein ecran'} className="p-1.5 hover:bg-white/10 rounded-xl transition-colors text-white/60 hover:text-white" aria-label={isFullscreen ? 'Reduire' : 'Plein ecran'}>
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <button onClick={() => { setIsFullscreen(false); toggleChat(); }} className="p-1.5 hover:bg-white/10 rounded-xl transition-colors" aria-label="Fermer">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </header>

            {/* Risk Meter (contextual) */}
            {activeContext && <RiskMeter score={ctxScore} verdict={ctxVerdict} />}

            {/* Context banner */}
            {activeContext && (
              <div className="bg-slate-50 border-b border-gray-100 px-4 py-2 flex items-center justify-between text-xs flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  {tc && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tc.dot}`} />}
                  <span className="text-gray-500 truncate">
                    <strong>{activeContext.type === 'url' ? 'URL' : activeContext.type === 'email' ? 'Email' : 'QR'}</strong>
                    {' · '}{ctxScore}/100
                    {activeContext.url && <> · <span className="font-mono">{activeContext.url.slice(0, 28)}…</span></>}
                  </span>
                </div>
                <div className="flex gap-2 flex-shrink-0 ml-2">
                  <button onClick={() => handleSendMessage('Explique ce score')} className="text-indigo-600 font-bold hover:underline">Expliquer</button>
                  <button onClick={() => setActiveContext(null)} className="text-gray-400 hover:text-gray-600" aria-label="Retirer contexte"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}

            {/* Quick actions */}
            {!activeContext && (
              <div className="px-4 py-2 bg-gradient-to-r from-slate-50 to-indigo-50/30 border-b border-gray-100 flex gap-2 flex-shrink-0">
                {QUICK_ACTIONS.filter(a => !a.needsCtx).map(action => (
                  <button key={action.id} onClick={() => handleSendMessage(action.msg)}
                    className="flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 px-2.5 py-1 rounded-full transition-all active:scale-95">
                    <action.icon className="w-3 h-3" />{action.label}
                  </button>
                ))}
              </div>
            )}
            {activeContext && (
              <div className="px-4 py-2 bg-gradient-to-r from-slate-50 to-indigo-50/30 border-b border-gray-100 flex gap-2 flex-shrink-0">
                {QUICK_ACTIONS.map(action => (
                  <button key={action.id} onClick={() => handleSendMessage(action.msg)}
                    className="flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 px-2.5 py-1 rounded-full transition-all active:scale-95">
                    <action.icon className="w-3 h-3" />{action.label}
                  </button>
                ))}
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/40" id="sentinelle-chat-messages">
              {messages.map(m => m.sender === 'bot'
                ? <BotMessage key={m.id} message={m} isLatest={m.id === latestBotId} />
                : <UserMessage key={m.id} message={m} />
              )}
              <AnimatePresence>{isLoading && <TypingIndicator />}</AnimatePresence>
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && !isLoading && (
              <div className="px-3 py-2 bg-white flex flex-wrap gap-1.5 border-t border-gray-100 flex-shrink-0">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => handleSendMessage(s)}
                    className="text-[10px] bg-slate-50 hover:bg-indigo-50 hover:text-indigo-600 text-gray-600 font-medium px-2.5 py-1 rounded-full border border-gray-200/60 active:scale-95 transition-all truncate max-w-[180px]"
                    title={s}>{s}</button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={e => { e.preventDefault(); handleSendMessage(); }}
              className="p-3 bg-white border-t border-gray-100 flex gap-2 items-center flex-shrink-0">
              <input
                ref={inputRef}
                type="text"
                id="sentinelle-chat-input"
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                placeholder={activeContext ? "Question sur cette analyse..." : "Posez votre question..."}
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 text-xs bg-slate-50 border border-gray-200 rounded-2xl focus:outline-none focus:border-indigo-400 focus:bg-white transition-all disabled:opacity-60"
              />
              <button type="submit" id="sentinelle-chat-send"
                disabled={!inputMessage.trim() || isLoading}
                className="w-9 h-9 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-2xl flex items-center justify-center transition-colors active:scale-95 flex-shrink-0"
                aria-label="Envoyer">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FAB Button (hidden in fullscreen) ── */}
      {!isFullscreen && (
      <motion.button
        id="sentinelle-chat-toggle"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={toggleChat}
        className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-2xl relative cursor-pointer border transition-all duration-300 ${
          isOpen
            ? 'bg-white border-gray-200 text-gray-700'
            : hasNewBadge
              ? 'bg-gradient-to-tr from-red-600 via-indigo-700 to-indigo-800 text-white border-transparent shadow-indigo-500/50'
              : 'bg-gradient-to-tr from-blue-700 via-indigo-700 to-indigo-800 text-white border-transparent shadow-indigo-500/30'
        }`}
        aria-label={isOpen ? "Fermer le chat" : "Ouvrir l'assistant cybersécurité"}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="w-6 h-6 md:w-7 md:h-7" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <MessageSquare className="w-6 h-6 md:w-7 md:h-7" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Badge nouveauté */}
        {!isOpen && hasNewBadge && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-[8px] font-bold text-white items-center justify-center">!</span>
          </span>
        )}

        {/* Glow pulse quand contexte actif */}
        {!isOpen && activeContext && (
          <span className="absolute inset-0 rounded-full animate-ping bg-indigo-400 opacity-20" />
        )}
      </motion.button>
      )}
    </div>
  );
}
